import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Heart, MessageCircle, Flag, ExternalLink, Pencil, Trash2, X, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { resolveImageUrl } from '@/lib/api';
import { VerifiedBadge } from '@/components/professional/VerifiedBadge';
import { PostComments } from '@/components/feed/PostComments';

const MOOD_EMOJI = {
  calmo: '😌',
  ansioso: '😰',
  motivado: '💪',
  grato: '🙏',
  triste: '😢',
};

const MOOD_COLORS = {
  calmo: 'bg-blue-100 text-blue-800',
  ansioso: 'bg-yellow-100 text-yellow-800',
  motivado: 'bg-green-100 text-green-800',
  grato: 'bg-purple-100 text-purple-800',
  triste: 'bg-gray-100 text-gray-800',
};

export function PostCard({ post, onLike, onComment, onReport, onEdit, onDelete, currentUserId, isAdmin }) {
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [showComments, setShowComments] = useState(false);
  const [extraComments, setExtraComments] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [isDeleting, setIsDeleting] = useState(false);

  // Verificar se é o próprio post do usuário
  const isOwnPost = currentUserId && post.user_id === currentUserId;
  const canDelete = isOwnPost || isAdmin;

  const handleLikeClick = async () => {
    if (isLiking) return;

    setIsLiking(true);
    const wasLiked = isLiked;
    const previousCount = likesCount;

    // Optimistic update
    setIsLiked(!wasLiked);
    setLikesCount(wasLiked ? likesCount - 1 : likesCount + 1);

    try {
      await onLike(post.id, !wasLiked);
    } catch (error) {
      // Revert on error
      setIsLiked(wasLiked);
      setLikesCount(previousCount);
      console.error('Failed to like post:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    try {
      await onEdit(post.id, editContent);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to edit post:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja apagar este post?')) return;
    setIsDeleting(true);
    try {
      await onDelete(post.id, isAdmin && !isOwnPost);
    } catch (error) {
      console.error('Failed to delete post:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ptBR,
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Card className="p-4 mb-4" data-testid={`post-card-${post.id}`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <Avatar data-testid="post-avatar">
          <AvatarImage src={resolveImageUrl(post.user_avatar)} alt={post.user_name} />
          <AvatarFallback>{getInitials(post.user_name)}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm" data-testid="post-author-name">
              {post.user_name}
            </h3>
            <VerifiedBadge show={post.user_professional_verified} testid="post-verified-badge" />
            {post.mood && (
              <Badge
                variant="secondary"
                className={MOOD_COLORS[post.mood]}
                data-testid="post-mood-badge"
              >
                {MOOD_EMOJI[post.mood]} {post.mood.charAt(0).toUpperCase() + post.mood.slice(1)}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <time dateTime={post.created_at} data-testid="post-timestamp">
              {formatDate(post.created_at)}
            </time>
            {post.group_name && (
              <>
                <span>•</span>
                <span data-testid="post-group-name">{post.group_name}</span>
              </>
            )}
          </div>
        </div>

        {/* Edit/Delete actions in header */}
        <div className="flex items-center gap-1 shrink-0">
          {isOwnPost && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setIsEditing(true); setEditContent(post.content); }}
              data-testid="post-edit-button"
              aria-label="Editar post"
              className="h-8 w-8 p-0"
            >
              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              data-testid="post-delete-button"
              aria-label="Apagar post"
              className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-3">
        {isEditing ? (
          <div className="space-y-2" data-testid="post-edit-form">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[80px]"
              maxLength={1000}
              data-testid="post-edit-textarea"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} data-testid="post-edit-cancel">
                <X className="h-4 w-4 mr-1" /> Cancelar
              </Button>
              <Button size="sm" onClick={handleEdit} disabled={!editContent.trim()} data-testid="post-edit-save">
                <Check className="h-4 w-4 mr-1" /> Salvar
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap break-words" data-testid="post-content">
            {post.content}
          </p>
        )}

        {/* Link */}
        {post.link_url && (
          <a
            href={post.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
            data-testid="post-link"
            aria-label={`Link externo: ${post.link_url}`}
          >
            <ExternalLink className="h-3 w-3" />
            {new URL(post.link_url).hostname}
          </a>
        )}

        {/* Media Grid */}
        {post.media_urls && post.media_urls.length > 0 && (
          <div
            className={`grid gap-2 mt-3 ${
              post.media_urls.length === 1
                ? 'grid-cols-1'
                : post.media_urls.length === 2
                ? 'grid-cols-2'
                : 'grid-cols-2'
            }`}
            data-testid="post-media-grid"
          >
            {post.media_urls.slice(0, 4).map((url, index) => (
              <div
                key={index}
                className={`relative ${
                  post.media_urls.length === 3 && index === 0 ? 'col-span-2' : ''
                } aspect-square`}
              >
                <img
                  src={resolveImageUrl(url)}
                  alt={`Imagem ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator className="mb-3" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLikeClick}
          disabled={isLiking}
          className={isLiked ? 'text-red-500 hover:text-red-600' : ''}
          data-testid="post-like-button"
          aria-label={isLiked ? 'Remover curtida' : 'Curtir post'}
          aria-pressed={isLiked}
        >
          <Heart className={`h-4 w-4 mr-1 ${isLiked ? 'fill-current' : ''}`} />
          <span data-testid="post-likes-count">{likesCount}</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowComments(!showComments)}
          data-testid="post-comment-button"
          aria-label="Comentar"
          aria-expanded={showComments}
        >
          <MessageCircle className="h-4 w-4 mr-1" />
          <span data-testid="post-comments-count">{post.comments_count + extraComments}</span>
        </Button>

        <div className="flex-1" />

        {/* Só mostrar botão de denúncia se não for o próprio post */}
        {!isOwnPost && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReport(post.id)}
            data-testid="post-report-button"
            aria-label="Denunciar post"
          >
            <Flag className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Comments Section (placeholder for now) */}
      {showComments && <PostComments postId={post.id} onAdded={() => setExtraComments((n) => n + 1)} />}
    </Card>
  );
}
